import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarDays, Repeat } from 'lucide-react';
import { fetchOggi, toggleAzione, toggleMit, applyOperations, deleteMit, deleteAzione, setRecurrenceOverride } from '../lib/api';
import { useOptimisticToggle } from '../lib/useOptimisticToggle';
import { useUrlFilters } from '../lib/useUrlFilters';
import { AREE } from '../constants/areas';
import Breadcrumb from './nav/Breadcrumb';
import PageSkeleton from './nav/PageSkeleton';
import Checkbox from './nav/Checkbox';
import FilterBar from './nav/FilterBar';
import EmptyState from './nav/EmptyState';
import PromoteToMitButton from './nav/PromoteToMitButton';
import DemoteToAzioneButton from './nav/DemoteToAzioneButton';
import MovedBadge from './nav/MovedBadge';
import SuggestMitButton from './nav/SuggestMitButton';
import DateAssignPopover from './nav/DateAssignPopover';
import TaskTextEditor from './nav/TaskTextEditor';
import { formatSettimanaConRange } from '../lib/weekFormat';
import { daysOfIsoWeek } from '../lib/weekFromIsoDate';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';

const FADE_TRANS = 'opacity 280ms cubic-bezier(0.22, 1, 0.36, 1), text-decoration-color 280ms';

function fmtDataIt(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const giorni = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
  const mesi = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
  const dt = new Date(Date.UTC(parseInt(y), parseInt(m) - 1, parseInt(d)));
  return `${giorni[dt.getUTCDay()]} ${parseInt(d, 10)} ${mesi[parseInt(m, 10) - 1]} ${y}`;
}

export default function VistaOggi() {
  const [data, setData] = useState(null);
  const [mit, setMit] = useState([]);
  const [azioni, setAzioni] = useState([]);
  const [mitScaduti, setMitScaduti] = useState([]);
  const [azioniScadute, setAzioniScadute] = useState([]);
  const [loading, setLoading] = useState(true);
  // 2-click confirm per "Rischedula tutto" (evita mass-move accidentale).
  // pendingReschedule = null | 'today' | 'tomorrow' | 'next-monday' | 'spread'
  const [pendingReschedule, setPendingReschedule] = useState(null);
  const [reschedulingAll, setReschedulingAll] = useState(false);
  // Filtro area (stato + confidence non applicabili in vista Oggi) — URL-persisted
  const [filters, setFilters] = useUrlFilters();

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchOggi();
      setData(d);
      setMit(d.mit_oggi || []);
      setAzioni(d.azioni_settimana || []);
      setMitScaduti(d.mit_scaduti || []);
      setAzioniScadute(d.azioni_scadute || []);
    } catch {
      toast.error('Errore caricamento vista Oggi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const reload = () => load();
    window.addEventListener('qp:quick-add-created', reload);
    return () => window.removeEventListener('qp:quick-add-created', reload);
  }, []);

  // Helper: distingue occorrenza virtuale (id composito `{parent}::YYYY-MM-DD`
  // generato da enrich_in_range) da riga reale. Le occorrenze non hanno un
  // record proprio su DB → il toggle NON deve chiamare /api/azione/{id}
  // (fallirebbe 500) ma /api/recurrence/override.
  const isOccurrence = (item) => !!(item && item.is_occurrence && typeof item.id === 'string' && item.id.includes('::'));

  const applyOccurrenceOverride = (setList, item, next, kind) => {
    setList((arr) => arr.map((x) => (x.id === item.id ? { ...x, fatto: next } : x)));
    return setRecurrenceOverride({
      parent_kind: kind,
      parent_id: item.parent_id,
      occurrence_date: item.occurrence_date,
      fatto: next,
    }).catch(() => {
      setList((arr) => arr.map((x) => (x.id === item.id ? { ...x, fatto: !next } : x)));
      toast.error('Salvataggio occorrenza fallito');
    });
  };

  const _toggleMitRaw = useOptimisticToggle(setMit, (id, next) => toggleMit(id, next), 'fatto');
  const _toggleAzioneRaw = useOptimisticToggle(setAzioni, (id, next) => toggleAzione(id, next), 'fatto');
  const toggleMitItem = (id, next, item) => {
    if (isOccurrence(item)) return applyOccurrenceOverride(setMit, item, next, 'mit');
    return _toggleMitRaw(id, next);
  };
  const toggleAzioneItem = (id, next, item) => {
    if (isOccurrence(item)) return applyOccurrenceOverride(setAzioni, item, next, 'azione');
    return _toggleAzioneRaw(id, next);
  };
  // Handler dedicati per la sezione Scaduti: usano setMitScaduti/setAzioniScadute
  // (le liste "scadute" sono separate da mit/azioni). Al toggle a `fatto=true`
  // la task viene rimossa dalla lista scaduti (dopo un piccolo delay per
  // lasciare vedere l'animazione di spunta). Undo dal toast lascia comunque
  // la task off-list finché il prossimo `load()` non la ripescherà — ma solo
  // se ancora scaduta (dipende dal backend `.eq("fatto", False)`).
  const _toggleMitScadutoRaw = useOptimisticToggle(setMitScaduti, (id, next) => toggleMit(id, next), 'fatto');
  const _toggleAzioneScadutaRaw = useOptimisticToggle(setAzioniScadute, (id, next) => toggleAzione(id, next), 'fatto');
  const toggleMitScaduto = async (id, next, item) => {
    if (isOccurrence(item)) {
      await applyOccurrenceOverride(setMitScaduti, item, next, 'mit');
    } else {
      await _toggleMitScadutoRaw(id, next);
    }
    if (next) {
      setTimeout(() => {
        setMitScaduti((prev) => prev.filter((m) => m.id !== id));
      }, 500);
    }
  };
  const toggleAzioneScaduta = async (id, next, item) => {
    if (isOccurrence(item)) {
      await applyOccurrenceOverride(setAzioniScadute, item, next, 'azione');
    } else {
      await _toggleAzioneScadutaRaw(id, next);
    }
    if (next) {
      setTimeout(() => {
        setAzioniScadute((prev) => prev.filter((a) => a.id !== id));
      }, 500);
    }
  };

  const toggleAreaChip = (area) => {
    const cur = filters.area || [];
    setFilters({ ...filters, area: cur.includes(area) ? cur.filter((a) => a !== area) : [...cur, area] });
  };

  // Edit inline testo MIT/Azioni (click sul testo → input)
  const handleUpdateText = async (kind, id, newText) => {
    try {
      await applyOperations([{ entita: kind, azione: 'aggiorna', id, campi: { testo: newText } }]);
      toast.success(`${kind === 'mit' ? 'MIT' : 'Azione'} aggiornata`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aggiornamento testo fallito');
      throw e;
    }
  };

  // Delete hard MIT/Azioni (cestino con conferma)
  const handleDeleteTask = async (kind, id) => {
    try {
      if (kind === 'mit') await deleteMit(id);
      else await deleteAzione(id);
      toast.success(`${kind === 'mit' ? 'MIT' : 'Azione'} eliminata`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eliminazione fallita');
    }
  };

  // KR distinti derivati dalle azioni (memoizzato per evitare ricalcolo a ogni render).
  // Deve stare PRIMA degli early-return per rispettare rules-of-hooks.
  const krFromAzioni = useMemo(
    () => Array.from(
      new Map((azioni || []).filter((a) => a.kr).map((a) => [a.kr.id, a.kr])).values(),
    ),
    [azioni],
  );

  if (loading) {
    return <PageSkeleton sections={2} rowsPerSection={4} testid="oggi-skeleton" />;
  }
  if (!data) return null;

  const passesArea = (item) => {
    if (filters.area.length === 0) return true;
    const ar = ((item.obiettivo?.area) || item.area || '').toLowerCase();
    return filters.area.includes(ar);
  };

  const filteredMit = mit.filter(passesArea);
  const filteredAzioni = azioni.filter(passesArea);
  const filteredMitScaduti = mitScaduti.filter(passesArea);
  const filteredAzioniScadute = azioniScadute.filter(passesArea);

  // Rischedulazione batch delle task scadute. 4 modalità:
  //   'today'       → tutte a oggi
  //   'tomorrow'    → tutte a domani
  //   'next-monday' → tutte a lunedì della prossima settimana
  //   'spread'      → distribuite round-robin sui 5 giorni lavorativi
  //                   (lun→ven) della prossima settimana
  //
  // Ogni modalità usa il pattern 2-click confirm.
  const scheduleRescheduleMode = (mode) => {
    if (pendingReschedule !== mode) {
      setPendingReschedule(mode);
      setTimeout(() => {
        setPendingReschedule((cur) => (cur === mode ? null : cur));
      }, 4000);
      return;
    }
    setPendingReschedule(null);
    void executeReschedule(mode);
  };

  const executeReschedule = async (mode) => {
    if (reschedulingAll) return;
    setReschedulingAll(true);
    try {
      const today = data?.data;
      if (!today) throw new Error('missing today');
      const totOverdue = filteredMitScaduti.length + filteredAzioniScadute.length;
      const allOverdue = [
        ...filteredMitScaduti.map((m) => ({ ...m, __kind: 'mit' })),
        ...filteredAzioniScadute.map((a) => ({ ...a, __kind: 'azione' })),
      ];
      const assignments = [];
      if (mode === 'today') {
        for (const t of allOverdue) assignments.push({ task: t, target_date: today });
      } else if (mode === 'tomorrow') {
        const [yy, mm, dd] = today.split('-').map(Number);
        const tomorrow = new Date(Date.UTC(yy, mm - 1, dd + 1)).toISOString().slice(0, 10);
        for (const t of allOverdue) assignments.push({ task: t, target_date: tomorrow });
      } else if (mode === 'next-monday' || mode === 'spread') {
        const [yy, mm, dd] = today.split('-').map(Number);
        const todayD = new Date(Date.UTC(yy, mm - 1, dd));
        const dow = todayD.getUTCDay() || 7;
        const daysToNextMon = 8 - dow;
        todayD.setUTCDate(todayD.getUTCDate() + daysToNextMon);
        const nextMon = todayD.toISOString().slice(0, 10);
        const nextWeekLabel = weekFromIsoDate(nextMon);
        const weekDays = daysOfIsoWeek(nextWeekLabel).slice(0, 5);
        if (mode === 'next-monday') {
          for (const t of allOverdue) assignments.push({ task: t, target_date: nextMon });
        } else {
          allOverdue.forEach((t, i) => {
            assignments.push({ task: t, target_date: weekDays[i % 5] });
          });
        }
      } else {
        throw new Error(`modalità sconosciuta: ${mode}`);
      }
      const ops = assignments.map(({ task, target_date }) => {
        const campi = task.__kind === 'mit'
          ? { data: target_date }
          : { data: target_date, settimana: weekFromIsoDate(target_date) };
        return { entita: task.__kind, azione: 'aggiorna', id: task.id, campi };
      });
      if (ops.length === 0) return;
      await applyOperations(ops);
      const labelByMode = {
        today: 'a oggi',
        tomorrow: 'a domani',
        'next-monday': 'a lunedì prossimo',
        spread: 'distribuite sulla settimana',
      };
      toast.success(`${totOverdue} task rischedulate ${labelByMode[mode]}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rischedulazione batch fallita');
    } finally {
      setReschedulingAll(false);
    }
  };

  const filtersActive = filters.area.length > 0;

  // Split azioni tra "di oggi" (fissate al giorno corrente) e "della settimana"
  // (flottanti o fissate ad altri giorni). Serve a rendere immediato il focus
  // operativo: quello che DEVO fare oggi vs quello che c'è in giro.
  const oggiIso = data.data;
  const filteredAzioniOggi = filteredAzioni.filter((a) => a.data === oggiIso);
  const filteredAzioniSettimana = filteredAzioni.filter((a) => a.data !== oggiIso);

  // Vista "Oggi" = focus operativo: nascondi MIT e Azioni già completate.
  // Il counter resta calcolato sui filtered* (totale) così l'utente vede `2/5`
  // ma in lista compaiono solo le task ancora da fare.
  const visibleMit = filteredMit.filter((m) => !m.fatto);
  const visibleAzioniOggi = filteredAzioniOggi.filter((a) => !a.fatto);
  const visibleAzioniSettimana = filteredAzioniSettimana.filter((a) => !a.fatto);

  const mitTot = filteredMit.length;
  const mitDone = filteredMit.filter((x) => x.fatto).length;
  const aoTot = filteredAzioniOggi.length;
  const aoDone = filteredAzioniOggi.filter((x) => x.fatto).length;
  const asTot = filteredAzioniSettimana.length;
  const asDone = filteredAzioniSettimana.filter((x) => x.fatto).length;
  const mitPct = mitTot > 0 ? Math.round((mitDone / mitTot) * 1000) / 10 : null;
  const aoPct = aoTot > 0 ? Math.round((aoDone / aoTot) * 1000) / 10 : null;
  const asPct = asTot > 0 ? Math.round((asDone / asTot) * 1000) / 10 : null;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="max-w-7xl mx-auto px-6 pt-2">
        <Breadcrumb
          backTo="/"
          backLabel="Dashboard"
          items={[{ label: 'Dashboard', to: '/' }, { label: 'Oggi' }]}
        />
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-20">
        <header className="border-b border-line pb-8 mb-10" data-testid="oggi-header">
          <span className="mono-label">Focus operativo</span>
          <h1 className="font-display tracking-tight text-4xl md:text-5xl lg:text-6xl mt-2 mb-2">{fmtDataIt(data.data)}</h1>
          <div className="font-mono text-xs uppercase tracking-[0.15em] text-soft">
            <Link to={`/periodo/settimana/${data.settimana}`} className="hover:text-accent" data-testid="oggi-week-link">
              {formatSettimanaConRange(data.settimana)}
            </Link>
          </div>
        </header>

        {/* Filtri trasversali (solo area su questa vista) */}
        <FilterBar filters={filters} onChange={setFilters} hide={['stato', 'confidence']} />

        <section className="mb-16" data-testid="section-mit-oggi">
          <div className="flex items-baseline justify-between mb-4 border-b border-line pb-3">
            <h2 className="font-display text-2xl tracking-tight">Le 3 Most Important Task (MIT), oggi</h2>
            <div className="flex items-center gap-4">
              <SuggestMitButton
                mode="giorno"
                value={data.data}
                azioni={azioni}
                kr={krFromAzioni}
                onPromoted={(deletedId, mitCreato) => {
                  // Optimistic: rimuovi azione promossa dalla lista e aggiungi MIT
                  setAzioni((arr) => arr.filter((x) => x.id !== deletedId));
                  if (mitCreato && mitCreato.data === data.data) {
                    setMit((arr) => [...arr, mitCreato]);
                  }
                }}
                testid="oggi-suggerisci-mit-btn"
              />
              <span className="font-mono text-xs tabular-nums text-soft" data-testid="oggi-mit-counter">
                {mitDone}/{mitTot}{mitPct != null && ` · ${mitPct}%`}
                {filtersActive && mit.length !== mitTot && (
                  <span className="text-mute ml-2">(di {mit.length} totali)</span>
                )}
              </span>
            </div>
          </div>
          {filteredMit.length === 0 ? (
            filtersActive && mit.length > 0 ? (
              <p className="font-body text-sm text-mute italic" data-testid="oggi-mit-empty">
                Nessuna MIT per le aree selezionate.
              </p>
            ) : (
              <EmptyState
                title="Nessuna MIT per oggi"
                desc="Definisci 1-3 task ad alto impatto per la giornata. La domanda del coach: 'Cosa, se completato, renderà oggi una giornata vincente?'"
                actionLabel="Aggiungi MIT (⇧N)"
                actionOnClick={() => window.dispatchEvent(new CustomEvent('qp:quick-add-open'))}
                secondary={{ label: 'Pianifica la settimana', to: '/settimana' }}
                testid="oggi-mit-empty"
              />
            )
          ) : (
            <ul className="divide-y divide-linesoft border-b border-linesoft">
              {visibleMit.map((m) => (
                <Row
                  key={m.id}
                  id={m.id}
                  kind="mit"
                  done={m.fatto}
                  text={m.testo}
                  meta={m.kr}
                  obiettivo={m.obiettivo}
                  data={m.data}
                  isOccurrence={m.is_occurrence}
                  parentId={m.parent_id}
                  occurrenceDate={m.occurrence_date}
                  onToggle={toggleMitItem}
                  onAreaClick={toggleAreaChip}
                  onUpdateText={handleUpdateText}
                  onDeleteTask={handleDeleteTask}
                  onDemoted={load}
                  onAssignDate={async (mitId, currentVal, newVal) => {
                    if (!newVal || newVal === currentVal) return;
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(newVal)) {
                      toast.error('Le MIT richiedono un giorno specifico');
                      return;
                    }
                    try {
                      await applyOperations([{ entita: 'mit', azione: 'aggiorna', id: mitId, campi: { data: newVal } }]);
                      toast.success(`MIT spostata al ${newVal}`);
                      load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Spostamento MIT fallito');
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </section>

        <section className="mb-16" data-testid="section-azioni-oggi">
          <div className="flex items-baseline justify-between mb-4 border-b border-line pb-3">
            <h2 className="font-display text-2xl tracking-tight">Azioni di oggi</h2>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('qp:quick-add-open', {
                  detail: { kind: 'azione', dataIso: oggiIso },
                }))}
                className="font-mono text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 border border-line text-soft hover:border-ink hover:text-ink transition-colors"
                data-testid="oggi-add-azione-oggi-btn"
                title="Crea un'azione già fissata a oggi"
              >
                + Aggiungi azione per oggi
              </button>
              <span className="font-mono text-xs tabular-nums text-soft" data-testid="oggi-azioni-oggi-counter">
                {aoDone}/{aoTot}{aoPct != null && ` · ${aoPct}%`}
              </span>
            </div>
          </div>
          {filteredAzioniOggi.length === 0 ? (
            <p className="font-body text-sm text-mute italic" data-testid="oggi-azioni-oggi-empty">
              Nessuna azione fissata a oggi. Trascina un&apos;azione della settimana su questo giorno dal calendario oppure assegna una data.
            </p>
          ) : (
            <ul className="divide-y divide-linesoft border-b border-linesoft">
              {visibleAzioniOggi.map((a) => (
                <Row
                  key={a.id}
                  id={a.id}
                  kind="azione"
                  done={a.fatto}
                  text={a.testo}
                  meta={a.kr}
                  obiettivo={a.obiettivo}
                  areaFallback={a.area}
                  isBau={a.is_bau}
                  data={a.data}
                  settimana={a.settimana}
                  isOccurrence={a.is_occurrence}
                  parentId={a.parent_id}
                  occurrenceDate={a.occurrence_date}
                  onToggle={toggleAzioneItem}
                  onAreaClick={toggleAreaChip}
                  onPromoted={load}
                  onAssignDate={async (azId, currentVal, newVal) => {
                    if (!newVal || newVal === currentVal) return;
                    const isDate = /^\d{4}-\d{2}-\d{2}$/.test(newVal);
                    const campi = isDate
                      ? { data: newVal, settimana: weekFromIsoDate(newVal) }
                      : { settimana: newVal, data: null };
                    try {
                      await applyOperations([{ entita: 'azione', azione: 'aggiorna', id: azId, campi }]);
                      toast.success(isDate ? `Azione fissata al ${newVal}` : `Spostata a ${newVal.slice(-3)}`);
                      load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Spostamento azione fallito');
                    }
                  }}
                  onUpdateText={handleUpdateText}
                  onDeleteTask={handleDeleteTask}
                />
              ))}
            </ul>
          )}
        </section>

        <section data-testid="section-azioni-settimana">
          <div className="flex items-baseline justify-between mb-4 border-b border-line pb-3">
            <h2 className="font-display text-2xl tracking-tight">Azioni della settimana</h2>
            <span className="font-mono text-xs tabular-nums text-soft" data-testid="oggi-azioni-counter">
              {asDone}/{asTot}{asPct != null && ` · ${asPct}%`}
              {filtersActive && azioni.length !== (aoTot + asTot) && (
                <span className="text-mute ml-2">(di {azioni.length} totali)</span>
              )}
            </span>
          </div>
          {filteredAzioniSettimana.length === 0 ? (
            filtersActive && azioni.length > 0 ? (
              <p className="font-body text-sm text-mute italic" data-testid="oggi-azioni-empty">
                Nessuna azione per le aree selezionate.
              </p>
            ) : (
              <EmptyState
                title="Nessuna altra azione per questa settimana"
                desc="Se ti resta capacità dopo le azioni di oggi, aggiungine altre per i giorni successivi."
                actionLabel="Pianifica con il coach"
                actionTo="/pianifica-settimana"
                secondary={{ label: 'Aggiungi manualmente (⇧N)', onClick: () => window.dispatchEvent(new CustomEvent('qp:quick-add-open')) }}
                testid="oggi-azioni-empty"
              />
            )
          ) : (
            <ul className="divide-y divide-linesoft border-b border-linesoft">
              {visibleAzioniSettimana.map((a) => (
                <Row
                  key={a.id}
                  id={a.id}
                  kind="azione"
                  done={a.fatto}
                  text={a.testo}
                  meta={a.kr}
                  obiettivo={a.obiettivo}
                  areaFallback={a.area}
                  isBau={a.is_bau}
                  data={a.data}
                  settimana={a.settimana}
                  isOccurrence={a.is_occurrence}
                  parentId={a.parent_id}
                  occurrenceDate={a.occurrence_date}
                  onToggle={toggleAzioneItem}
                  onAreaClick={toggleAreaChip}
                  onPromoted={load}
                  onAssignDate={async (azId, currentVal, newVal) => {
                    if (!newVal || newVal === currentVal) return;
                    const isDate = /^\d{4}-\d{2}-\d{2}$/.test(newVal);
                    const campi = isDate
                      ? { data: newVal, settimana: weekFromIsoDate(newVal) }
                      : { settimana: newVal, data: null };
                    try {
                      await applyOperations([{ entita: 'azione', azione: 'aggiorna', id: azId, campi }]);
                      toast.success(isDate ? `Azione fissata al ${newVal}` : `Spostata a ${newVal.slice(-3)}`);
                      load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Spostamento azione fallito');
                    }
                  }}
                  onUpdateText={handleUpdateText}
                  onDeleteTask={handleDeleteTask}
                />
              ))}
            </ul>
          )}
        </section>

        {(filteredMitScaduti.length > 0 || filteredAzioniScadute.length > 0) && (
          <section data-testid="section-scaduti">
            <div className="flex items-baseline justify-between mb-4 border-b border-line pb-3 gap-3 flex-wrap">
              <h2 className="font-display text-2xl tracking-tight">
                Scadute
                <span className="ml-3 font-mono text-xs uppercase tracking-[0.18em] text-risk">
                  ⚠ da rischedulare
                </span>
              </h2>
              <div className="flex items-center gap-3">
                {pendingReschedule ? (
                  <button
                    type="button"
                    onClick={() => scheduleRescheduleMode(pendingReschedule)}
                    disabled={reschedulingAll}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-accent text-accent bg-accent/5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors disabled:opacity-50"
                    title="Click di nuovo per confermare"
                    data-testid="oggi-scaduti-reschedule-confirm-btn"
                  >
                    {reschedulingAll
                      ? 'Sposto...'
                      : `Sicuro? ${{
                          today: 'A oggi',
                          tomorrow: 'A domani',
                          'next-monday': 'A lun prossimo',
                          spread: 'Distribuisci lun→ven',
                        }[pendingReschedule]} · ${filteredMitScaduti.length + filteredAzioniScadute.length} task`}
                  </button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        disabled={reschedulingAll}
                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-line text-soft hover:border-ink hover:text-ink font-mono text-[10px] uppercase tracking-[0.18em] transition-colors disabled:opacity-50"
                        title={`Rischedula tutte le ${filteredMitScaduti.length + filteredAzioniScadute.length} task scadute`}
                        data-testid="oggi-scaduti-reschedule-all-btn"
                      >
                        {reschedulingAll ? 'Sposto...' : 'Rischedula tutto…'}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuItem
                        onClick={() => scheduleRescheduleMode('today')}
                        data-testid="oggi-scaduti-mode-today"
                        className="font-mono text-xs uppercase tracking-[0.15em] cursor-pointer"
                      >
                        A oggi
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => scheduleRescheduleMode('tomorrow')}
                        data-testid="oggi-scaduti-mode-tomorrow"
                        className="font-mono text-xs uppercase tracking-[0.15em] cursor-pointer"
                      >
                        A domani
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => scheduleRescheduleMode('next-monday')}
                        data-testid="oggi-scaduti-mode-next-monday"
                        className="font-mono text-xs uppercase tracking-[0.15em] cursor-pointer"
                      >
                        A lunedì prossimo
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => scheduleRescheduleMode('spread')}
                        data-testid="oggi-scaduti-mode-spread"
                        className="font-mono text-xs uppercase tracking-[0.15em] cursor-pointer"
                      >
                        Distribuisci lun→ven
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <span className="font-mono text-xs tabular-nums text-soft" data-testid="oggi-scaduti-counter">
                  {filteredMitScaduti.length + filteredAzioniScadute.length} elementi
                </span>
              </div>
            </div>
            {filteredMitScaduti.length > 0 && (
              <>
                <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mt-3 mb-2">
                  MIT scadute
                </h3>
                <ul className="divide-y divide-linesoft border-b border-linesoft mb-4">
                  {filteredMitScaduti.map((m) => (
                    <Row
                      key={`sc-mit-${m.id}`}
                      id={m.id}
                      kind="mit"
                      done={m.fatto}
                      text={m.testo}
                      meta={m.kr}
                      obiettivo={m.obiettivo}
                      data={m.data}
                      isOccurrence={m.is_occurrence}
                      parentId={m.parent_id}
                      occurrenceDate={m.occurrence_date}
                      onToggle={toggleMitScaduto}
                      onAreaClick={toggleAreaChip}
                      onUpdateText={handleUpdateText}
                      onDeleteTask={handleDeleteTask}
                      onDemoted={load}
                      onAssignDate={async (mitId, currentVal, newVal) => {
                        if (!newVal || newVal === currentVal) return;
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(newVal)) {
                          toast.error('Le MIT richiedono un giorno specifico');
                          return;
                        }
                        try {
                          await applyOperations([{ entita: 'mit', azione: 'aggiorna', id: mitId, campi: { data: newVal } }]);
                          toast.success(`MIT rischedulata al ${newVal}`);
                          load();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Rischedulazione MIT fallita');
                        }
                      }}
                    />
                  ))}
                </ul>
              </>
            )}
            {filteredAzioniScadute.length > 0 && (
              <>
                <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mt-3 mb-2">
                  Azioni scadute
                </h3>
                <ul className="divide-y divide-linesoft border-b border-linesoft">
                  {filteredAzioniScadute.map((a) => (
                    <Row
                      key={`sc-az-${a.id}`}
                      id={a.id}
                      kind="azione"
                      done={a.fatto}
                      text={a.testo}
                      meta={a.kr}
                      obiettivo={a.obiettivo}
                      areaFallback={a.area}
                      isBau={a.is_bau}
                      data={a.data}
                      settimana={a.settimana}
                      isOccurrence={a.is_occurrence}
                      parentId={a.parent_id}
                      occurrenceDate={a.occurrence_date}
                      onToggle={toggleAzioneScaduta}
                      onAreaClick={toggleAreaChip}
                      onPromoted={load}
                      onAssignDate={async (azId, currentVal, newVal) => {
                        if (!newVal || newVal === currentVal) return;
                        const isDate = /^\d{4}-\d{2}-\d{2}$/.test(newVal);
                        const campi = isDate
                          ? { data: newVal, settimana: weekFromIsoDate(newVal) }
                          : { settimana: newVal, data: null };
                        try {
                          await applyOperations([{ entita: 'azione', azione: 'aggiorna', id: azId, campi }]);
                          toast.success(isDate ? `Azione rischedulata al ${newVal}` : `Spostata a ${newVal.slice(-3)}`);
                          load();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Rischedulazione azione fallita');
                        }
                      }}
                      onUpdateText={handleUpdateText}
                      onDeleteTask={handleDeleteTask}
                    />
                  ))}
                </ul>
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function Row({ id, kind, done, text, meta, obiettivo, areaFallback, isBau, data, settimana, isOccurrence, parentId, occurrenceDate, onToggle, onAreaClick, onPromoted, onAssignDate, onUpdateText, onDeleteTask, onDemoted }) {
  const ameta = AREE[((obiettivo?.area) || areaFallback || '').toLowerCase()] || null;
  const areaKey = ((obiettivo?.area) || areaFallback || '').toLowerCase();
  const textStyle = {
    textDecoration: done ? 'line-through' : 'none',
    opacity: done ? 0.5 : 1,
    color: done ? 'var(--qp-text-mute)' : undefined,
    transition: FADE_TRANS,
  };
  return (
    <li className="group py-3 flex items-center gap-3" data-testid={`oggi-row-${kind}-${id}`}>
      <Checkbox
        checked={done}
        onToggle={(next) => onToggle(id, next, { id, is_occurrence: isOccurrence, parent_id: parentId, occurrence_date: occurrenceDate })}
        size={20}
        label={text}
        testid={`oggi-toggle-${kind}-${id}`}
      />
      <MovedBadge id={id} testid={`oggi-moved-badge-${kind}-${id}`} />
      {kind === 'azione' && isOccurrence && (
        <span
          className="shrink-0 inline-flex items-center justify-center w-5 h-5 border border-ink bg-ink text-canvas"
          title="Occorrenza ricorrente"
          aria-label="Occorrenza ricorrente"
          data-testid={`oggi-recurrence-indicator-azione-${id}`}
        >
          <Repeat size={10} strokeWidth={1.8} />
        </span>
      )}
      {ameta && (
        <button
          onClick={(e) => { e.stopPropagation(); onAreaClick && onAreaClick(areaKey); }}
          className="font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border shrink-0 underline decoration-dotted underline-offset-2 hover:no-underline hover:opacity-70 cursor-pointer"
          style={{ borderColor: ameta.color, color: ameta.color }}
          title={`Filtra per ${ameta.label}`}
          data-testid={`oggi-area-chip-${kind}-${id}`}
        >
          {isBau ? 'BAU' : ameta.label.slice(0, 3)}
        </button>
      )}
      {onUpdateText ? (
        <TaskTextEditor
          kind={kind}
          id={id}
          isOccurrence={!!isOccurrence}
          text={text}
          onSaveText={(nv) => onUpdateText(kind, id, nv)}
          onDelete={onDeleteTask ? () => onDeleteTask(kind, id) : null}
          textStyle={textStyle}
        />
      ) : (
        <span
          className="flex-1 font-body text-sm"
          style={textStyle}
        >
          {text}
        </span>
      )}
      {meta && obiettivo && (
        <Link
          to={`/obiettivo/${obiettivo.id}`}
          className="font-mono text-[10px] uppercase tracking-[0.15em] text-soft hover:text-accent shrink-0 truncate max-w-[200px]"
          data-testid={`oggi-link-kr-${kind}-${id}`}
          title={`${obiettivo.titolo} · ${meta.titolo}`}
        >
          → {meta.titolo}
        </Link>
      )}
      {kind === 'azione' && (data || settimana) && (
        <span
          className={`font-mono text-[10px] tabular-nums shrink-0 px-1.5 py-0.5 border ${
            data ? 'border-ink text-ink bg-canvas' : 'border-line text-mute'
          }`}
          title={data ? `Fissata al ${data}` : `Settimana ${settimana}`}
          data-testid={`oggi-azione-date-label-${id}`}
        >
          {data ? formatDateShort(data) : (settimana || '').replace('20', '').replace('-W', ' · W')}
        </span>
      )}
      {kind === 'mit' && data && (
        <span
          className="font-mono text-[10px] tabular-nums shrink-0 px-1.5 py-0.5 border border-ink text-ink bg-canvas"
          title={`MIT fissata al ${data}`}
          data-testid={`oggi-mit-date-label-${id}`}
        >
          {formatDateShort(data)}
        </span>
      )}
      {kind === 'azione' && onAssignDate && !isOccurrence && (
        <DateAssignPopover
          kind="azione"
          currentValue={data || settimana || ''}
          onSave={(nv) => onAssignDate(id, data || settimana || '', nv)}
          side="left"
          testid={`oggi-azione-date-popover-${id}`}
        >
          <button
            type="button"
            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 border transition-colors ${
              data
                ? 'border-ink text-ink hover:bg-ink hover:text-canvas'
                : 'border-line text-mute hover:border-ink hover:text-ink'
            }`}
            title={data
              ? `Azione fissata al ${data} · clicca per cambiare`
              : 'Assegna data specifica o sposta settimana'}
            aria-label="Assegna data"
            data-testid={`oggi-azione-date-btn-${id}`}
          >
            <CalendarDays size={11} strokeWidth={1.6} />
          </button>
        </DateAssignPopover>
      )}
      {kind === 'mit' && onAssignDate && !isOccurrence && (
        <DateAssignPopover
          kind="mit"
          currentValue={data || ''}
          onSave={(nv) => onAssignDate(id, data || '', nv)}
          side="left"
          testid={`oggi-mit-date-popover-${id}`}
        >
          <button
            type="button"
            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 border transition-colors ${
              data
                ? 'border-ink text-ink hover:bg-ink hover:text-canvas'
                : 'border-line text-mute hover:border-ink hover:text-ink'
            }`}
            title={data
              ? `MIT fissata al ${data} · clicca per cambiare giorno`
              : 'Sposta MIT a un altro giorno'}
            aria-label="Sposta MIT"
            data-testid={`oggi-mit-date-btn-${id}`}
          >
            <CalendarDays size={11} strokeWidth={1.6} />
          </button>
        </DateAssignPopover>
      )}
      {kind === 'azione' && (
        <PromoteToMitButton
          azioneId={id}
          onPromoted={onPromoted}
          testid={`oggi-promote-mit-${id}`}
        />
      )}
      {kind === 'mit' && onDemoted && (
        <DemoteToAzioneButton
          mitId={id}
          onDemoted={onDemoted}
          testid={`oggi-demote-azione-${id}`}
        />
      )}
    </li>
  );
}

/** Ricava la settimana ISO YYYY-Www da YYYY-MM-DD. */
function weekFromIsoDate(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const wn = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(wn).padStart(2, '0')}`;
  } catch { return null; }
}

/** Formatta YYYY-MM-DD come "mar 30 giu" (ITA short). */
function formatDateShort(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return iso; }
}
